// Motor Sizing Tool: field metadata + client-side per-field validation, used for
// instant inline feedback as the user types. The actual sizing math and the
// authoritative (server-side) validation live in the main backend — see
// src/lib/toolsApi.js (motorToolApi) and backend/tools/motor_sizing_tool.py.

import MOTOR_PRESETS_DATA from './evMotorPresets.json';

export const DEFAULT_INPUTS = {
  vehicleMassKg: 1500,
  payloadMassKg: 100,
  rotationalMassFactor: 1.05,

  dragCoefficient: 0.30,
  frontalAreaM2: 2.2,
  airDensityKgM3: 1.225,
  rollingResistanceCoefficient: 0.015,

  tireFrictionCoefficient: 0.9,
  driveAxleLoadShare: 0.55,

  maxSpeedKmph: 150,
  accelTargetSpeedKmph: 100,
  accelTimeS: 8,
  constantPowerSpeedRatio: 3.0,

  continuousSpeedKmph: 120,
  sustainedGradePercent: 5,
  sustainedGradeSpeedKmph: 80,
  hillStartGradePercent: 25,

  wheelRadiusM: 0.30,
  gearRatio: 8.0,
  gearboxEfficiency: 0.97,
  motorInverterEfficiency: 0.90,
  dcBusVoltageV: 400,

  safetyMarginPercent: 15,
};

// Vehicle presets (evMotorPresets.json): each stores only the fields that differ from
// DEFAULT_INPUTS. The first entry is the generic default, so the page opens on the same
// values as the user guide's worked example.
export const MOTOR_PRESETS = MOTOR_PRESETS_DATA.map((p) => ({
  ...p,
  inputs: { ...DEFAULT_INPUTS, ...p.overrides },
}));
export const DEFAULT_PRESET_ID = MOTOR_PRESETS[0].id;

export function findPreset(id) {
  return MOTOR_PRESETS.find((p) => p.id === id) || null;
}

// min / max / strictlyPositive mirror FIELD_SPECS in backend/core/lookup/motor_specs.py.
export const FIELD_SPECS = [
  { key: 'vehicleMassKg', label: 'Vehicle Mass', unit: 'kg', group: 'vehicle', min: 200, max: 6000, strictlyPositive: false },
  { key: 'payloadMassKg', label: 'Payload Mass', unit: 'kg', group: 'vehicle', min: 0, max: 2000, strictlyPositive: false },
  { key: 'rotationalMassFactor', label: 'Rotational Mass Factor', unit: 'δ', group: 'vehicle', min: 1.0, max: 1.3, strictlyPositive: false },

  { key: 'dragCoefficient', label: 'Drag Coefficient', unit: 'Cd', group: 'aero', min: 0.10, max: 1.00, strictlyPositive: false },
  { key: 'frontalAreaM2', label: 'Frontal Area', unit: 'm²', group: 'aero', min: 0.50, max: 6.00, strictlyPositive: false },
  { key: 'airDensityKgM3', label: 'Air Density', unit: 'kg/m³', group: 'aero', min: 0.90, max: 1.50, strictlyPositive: false },
  { key: 'rollingResistanceCoefficient', label: 'Rolling Resistance Coefficient', unit: 'Crr', group: 'aero', min: 0.003, max: 0.05, strictlyPositive: false },

  { key: 'tireFrictionCoefficient', label: 'Tire-Road Friction Coefficient', unit: 'μ', group: 'traction', min: 0.3, max: 1.2, strictlyPositive: false },
  { key: 'driveAxleLoadShare', label: 'Drive-Axle Load Share', unit: '0-1', group: 'traction', min: 0.3, max: 1.0, strictlyPositive: false },

  { key: 'maxSpeedKmph', label: 'Max Speed', unit: 'km/h', group: 'performance', min: 20, max: 400, strictlyPositive: false },
  { key: 'accelTargetSpeedKmph', label: 'Acceleration Target Speed', unit: 'km/h', group: 'performance', min: 10, max: 300, strictlyPositive: false },
  { key: 'accelTimeS', label: 'Acceleration Time', unit: 's', group: 'performance', min: 1, max: 60, strictlyPositive: false },
  { key: 'constantPowerSpeedRatio', label: 'Constant-Power Speed Ratio', unit: 'x', group: 'performance', min: 1.5, max: 6.0, strictlyPositive: false },

  { key: 'continuousSpeedKmph', label: 'Continuous Cruise Speed', unit: 'km/h', group: 'duty', min: 10, max: 300, strictlyPositive: false },
  { key: 'sustainedGradePercent', label: 'Sustained Grade', unit: '%', group: 'duty', min: 0, max: 30, strictlyPositive: false },
  { key: 'sustainedGradeSpeedKmph', label: 'Sustained Grade Speed', unit: 'km/h', group: 'duty', min: 5, max: 200, strictlyPositive: false },
  { key: 'hillStartGradePercent', label: 'Hill-Start Grade', unit: '%', group: 'duty', min: 0, max: 50, strictlyPositive: false },

  { key: 'wheelRadiusM', label: 'Wheel Radius', unit: 'm', group: 'drivetrain', min: 0.15, max: 0.60, strictlyPositive: false },
  { key: 'gearRatio', label: 'Gear Ratio', unit: ':1', group: 'drivetrain', min: 1.0, max: 20.0, strictlyPositive: false },
  { key: 'gearboxEfficiency', label: 'Gearbox Efficiency', unit: '0-1', group: 'drivetrain', min: 0.85, max: 0.99, strictlyPositive: false },
  { key: 'motorInverterEfficiency', label: 'Motor + Inverter Efficiency', unit: '0-1', group: 'drivetrain', min: 0.70, max: 0.98, strictlyPositive: false },
  { key: 'dcBusVoltageV', label: 'DC Bus Voltage', unit: 'V', group: 'drivetrain', min: 24, max: 1000, strictlyPositive: false },

  { key: 'safetyMarginPercent', label: 'Safety Margin', unit: '%', group: 'margin', min: 0, max: 100, strictlyPositive: false },
];

// Speed fields that must not exceed Max Speed — mirrors _SPEEDS_CAPPED_BY_MAX in
// backend/tools/motor_sizing_tool.py.
export const SPEEDS_CAPPED_BY_MAX = [
  ['accelTargetSpeedKmph', 'Acceleration target speed'],
  ['continuousSpeedKmph', 'Continuous cruise speed'],
  ['sustainedGradeSpeedKmph', 'Sustained grade speed'],
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
